import { useAccount } from '@copilot/common/pages/accountDashboard/data/hook';
import { useParams } from 'react-router';
import { Button, Col, Row, Space, Tooltip, Skeleton, Modal } from 'antd';
import Text from 'antd/lib/typography/Text';
import { AccountDetailPageHeader } from './accountDetailsPageHeader';
import { useContext } from 'react';
import { ThemeContext } from 'styled-components';
import { isUndefined } from 'lodash';
import { useFeatureToggle } from '@copilot/common/hooks/feature';
import { Features } from '@copilot/data/responses/interface';
import { useImpersonationWithNewTab } from '@copilot/common/pages/adminHub/useImpersonation';
import { UserTable } from './userTable';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Icon } from '@iconify/react';
import { newWindowIcon } from '@copilot/common/constant/icons';
import { AccountDetail, UserDetail } from './types';
import { pluralString } from '@copilot/common/utils/stringFormat';
import { useUpdateAccountNameMutation } from '@copilot/data/graphql/_generated';
import notificationManager from '@copilot/common/utils/notificationManager';
import { useUserActivateUpdate } from '@copilot/common/pages/accountDetailsPage/hooks/useUserActivateUpdate';
import ErrorIcon from '@copilot/common/components/icon/errorIcon';
import { AccountManager } from '../../../../core/data/index';
import NotificationManager from '@copilot/common/utils/notificationManager';

type AccountDetailsRouterParams = {
	/** account id*/
	accountId: string;
};

/**
 * Page for Account details
 * @constructor
 */
export default function AccountDetailPage() {
	const { accountId } = useParams<AccountDetailsRouterParams>();
	const [{ data, loading }, refetch] = useAccount(accountId);
	const account = data?.account;
	const theme = useContext(ThemeContext);
	const [modal, contextHolder] = Modal.useModal();
	const isDeleteEnabled = useFeatureToggle(Features.AccountDeleteFeature);
	const { onActivate, onDeactivate } = useUserActivateUpdate(modal);
	const [updateAccountName, { loading: updatingAccountName }] = useUpdateAccountNameMutation();

	const onImpersonation = useImpersonationWithNewTab();

	if (loading) {
		return (
			<div style={{ minWidth: '100%', minHeight: '100%' }}>
				<AccountDetailsHeaderSkeleton />
			</div>
		);
	}

	if (isUndefined(account)) throw new Error('Account cannot be found');

	function handleImpersonation(path: string) {
		if (isUndefined(account) || isUndefined(account?.primaryContact)) {
			throw new Error('Account should have primary contact to impersonate');
		}
		onImpersonation(account.primaryContact.id, account.orgId, path);
	}

	function onUpdateName(accountName: string) {
		void updateAccountName({
			variables: { input: { accountId, accountName } },
			refetchQueries: ['account'],
		})
			.then(() => {
				notificationManager.showSuccessNotification({
					message: 'Account name updated',
				});
			})
			.catch((error) => {
				notificationManager.showErrorNotification({
					message: 'Update failed',
					description: error.message,
				});
			});
	}

	function onDelete(user: UserDetail) {
		modal.confirm({
			okText: 'Delete User',
			okButtonProps: { danger: true },
			title: 'Delete User',
			cancelText: 'Cancel',
			icon: <ErrorIcon />,
			width: 450,
			content: (
				<p>
					Are you sure you want to delete <b>{user.userName}</b>? Deleting will prevent
					this user from accessing the product and their associated data and campaigns
					will be removed.
				</p>
			),
			onOk: async () => {
				try {
					await AccountManager.deleteUser(user.id);
					NotificationManager.showSuccessNotification({
						message: 'User successfully deleted',
					});
					void refetch();
				} catch (e) {
					NotificationManager.showErrorNotification({
						message: `Error deleting ${user.userName}. Please contact support.`,
					});
				}
			},
		});
	}

	return (
		<div style={{ minWidth: '100%', minHeight: '100%' }}>
			<AccountDetailPageHeader
				name={account.name}
				status={account.status}
				impersonateDisabled={isUndefined(account.primaryContact?.id)}
				onImpersonation={() => handleImpersonation('/allcampaigns')}
				updatingName={updatingAccountName}
				onUpdateName={onUpdateName}
			/>
			<div
				style={{
					margin: theme['@spacer-size-md'],
					padding: theme['@spacer-size-md'],
					backgroundColor: 'white',
				}}
			>
				<Space direction="vertical" size="large">
					<UserTableHeader account={account} handleImpersonation={handleImpersonation} />
					<UserTable
						data={account.users}
						onImpersonation={handleImpersonation}
						onActivate={onActivate}
						onDeactivate={onDeactivate}
						onDelete={isDeleteEnabled ? onDelete : undefined}
					/>
				</Space>
			</div>
			{contextHolder}
		</div>
	);
}

/**
 * Skeleton for account details header when loading
 */
function AccountDetailsHeaderSkeleton() {
	const theme = useContext(ThemeContext);

	return (
		<Row
			style={{
				padding: `${theme['@spacer-size-lg']} ${theme['@spacer-size-md']}`,
				backgroundColor: 'white',
				justifyContent: 'space-between',
			}}
			align="middle"
		>
			<Skeleton.Input active />
			<Skeleton.Button active />
		</Row>
	);
}

type OnImpersonationCallback = (path: string) => void;

function UserTableHeader({
	account,
	handleImpersonation,
}: {
	account: AccountDetail;
	handleImpersonation: OnImpersonationCallback;
}) {
	const theme = useContext(ThemeContext);
	return (
		<Row align="middle" justify="space-between">
			<Col>
				<Space size={theme['@spacer-num-xs']} direction="vertical">
					<Row>
						<Space>
							<Text strong style={{ fontSize: theme['@font-size-base'] }}>
								{`${account.name} Team Users`}
							</Text>
							<Tooltip title="The list of users is identical to the list found under the 'Team Members' tab in the Prospecting platform.">
								<InfoCircleOutlined />
							</Tooltip>
						</Space>
					</Row>
					<Row>
						<span
							style={{
								color: theme['@Neutral/90'],
							}}
						>
							{`${account.activeUsers} active ${pluralString(
								'user',
								account.activeUsers
							)} / ${account.users.length} ${pluralString(
								'user',
								account.users.length
							)}`}
						</span>
					</Row>
				</Space>
			</Col>
			<Col>
				<Button
					onClick={() => {
						handleImpersonation('/?tab=teammembers');
					}}
				>
					<Space style={{ lineHeight: theme['@font-size-base'] }}>
						Add User <Icon icon={newWindowIcon} fontSize={theme['@font-size-base']} />
					</Space>
				</Button>
			</Col>
		</Row>
	);
}
